export interface ChangelogItem {
  field: string
  from: string | null
  to: string | null
}

export interface ChangelogEntry {
  at: string
  by?: string | null
  items: ChangelogItem[]
}

export interface DueTimelineEntry {
  at: string
  kind: 'set' | 'cleared' | 'slips' | 'pulled_in'
  from: string | null
  to: string | null
  days: number | null
}

export interface DueHistory {
  changes: number
  slips: number
  pulledIn: number
  set: number
  cleared: number
  firstCommitted: string | null
  netSlipDays: number | null
  lastChange: string | null
  timeline: DueTimelineEntry[]
  unparsed: Array<{ at: string, value: string }>
  unparsedFrom: string | null
}

export interface ItemHistory {
  due: DueHistory
  enteredRegister: string | null
  labelRemoved: string | null
  assigneeChanges: number
  assigneeTimeline: Transition[]
  scoreEvents: Transition[]
  categoryEvents: Transition[]
  statusTimeline: Transition[]
  projectMoves: Transition[]
}

export interface Transition {
  at: string
  by: string | null
  from: string | null
  to: string | null
}

export class UnparseableDateError extends Error {}

const REGISTER_LABEL = 'RiskRegister'
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/
const DMY_DATE = /^(\d{1,2})\/([A-Za-z]{3})\/(\d{2,4})$/
const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
}

function day(value: string): string {
  return value.slice(0, 10)
}

function padded(value: number, length: number): string {
  const text = String(value)
  return text.length >= length ? text : `${'0'.repeat(length - text.length)}${text}`
}

function utcDay(value: string): number {
  return Date.parse(`${value}T00:00:00Z`)
}

function daysBetween(from: string, to: string): number {
  return Math.round((utcDay(to) - utcDay(from)) / 86400000)
}

function asLabels(value: string | null): Set<string> {
  return new Set((value || '').split(/\s+/).filter(Boolean))
}

export function parseDate(value: string | null): string | null {
  if (value === null || value.trim() === '') return null
  const text = value.trim()
  const iso = ISO_DATE.exec(text)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`

  const dmy = DMY_DATE.exec(text)
  if (dmy) {
    const month = MONTHS[dmy[2].toLowerCase()]
    if (month) {
      const year = Number(dmy[3]) < 100 ? Number(dmy[3]) + 2000 : Number(dmy[3])
      return `${padded(year, 4)}-${padded(month, 2)}-${padded(Number(dmy[1]), 2)}`
    }
  }
  throw new UnparseableDateError(value)
}

export function clipHistory(entries: ChangelogEntry[], updated: string | null | undefined): ChangelogEntry[] {
  const horizon = updated ? Date.parse(updated.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2')) : NaN
  return entries
    .filter((entry) => {
      const timestamp = Date.parse(entry.at.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2'))
      return !Number.isNaN(timestamp) && (Number.isNaN(horizon) || timestamp <= horizon)
    })
    .slice()
    .sort((left, right) => left.at.localeCompare(right.at))
}

export function dueHistory(entries: ChangelogEntry[], currentDue?: string | null): DueHistory {
  const out: DueHistory = {
    changes: 0, slips: 0, pulledIn: 0, set: 0, cleared: 0,
    firstCommitted: null, netSlipDays: null, lastChange: null,
    timeline: [], unparsed: [], unparsedFrom: null,
  }

  for (const entry of entries) {
    for (const item of entry.items) {
      if (item.field !== 'duedate') continue
      let from: string | null
      let to: string | null
      try {
        from = parseDate(item.from)
        to = parseDate(item.to)
      } catch (error) {
        if (!(error instanceof UnparseableDateError)) throw error
        const at = day(entry.at)
        out.unparsed.push({ at, value: error.message })
        if (out.unparsedFrom === null || at < out.unparsedFrom) out.unparsedFrom = at
        continue
      }
      if (from === to) continue

      const kind: DueTimelineEntry['kind'] = from === null
        ? 'set'
        : to === null
          ? 'cleared'
          : to > from
            ? 'slips'
            : 'pulled_in'
      if (kind === 'pulled_in') out.pulledIn++
      else if (kind === 'slips') out.slips++
      else if (kind === 'set') out.set++
      else out.cleared++
      out.changes++
      out.lastChange = day(entry.at)
      if (out.firstCommitted === null) out.firstCommitted = from || to
      out.timeline.push({
        at: day(entry.at),
        kind,
        from,
        to,
        days: from && to ? daysBetween(from, to) : null,
      })
    }
  }

  const current = currentDue ? parseDate(currentDue) : null
  if (out.firstCommitted && current) out.netSlipDays = daysBetween(out.firstCommitted, current)
  return out
}

export function enteredRegister(entries: ChangelogEntry[]): string | null {
  for (const entry of entries) {
    for (const item of entry.items) {
      if (item.field !== 'labels') continue
      if (asLabels(item.to).has(REGISTER_LABEL) && !asLabels(item.from).has(REGISTER_LABEL)) return day(entry.at)
    }
  }
  return null
}

export function labelRemoved(entries: ChangelogEntry[]): string | null {
  let removed: string | null = null
  for (const entry of entries) {
    for (const item of entry.items) {
      if (item.field === 'labels' && asLabels(item.from).has(REGISTER_LABEL) && !asLabels(item.to).has(REGISTER_LABEL)) {
        removed = day(entry.at)
      }
    }
  }
  return removed
}

export function transitions(entries: ChangelogEntry[], field: string): Transition[] {
  const output: Transition[] = []
  entries.forEach((entry) => {
    entry.items
      .filter((item) => item.field === field && item.from !== item.to)
      .forEach((item) => output.push({ at: day(entry.at), by: entry.by || null, from: item.from, to: item.to }))
  })
  return output
}

export function statusOn(entries: ChangelogEntry[], currentStatus: string, onDate: string): string {
  const statusTransitions = transitions(entries, 'status')
  if (!statusTransitions.length) return currentStatus
  let held = statusTransitions[0].from || currentStatus
  for (const transition of statusTransitions) {
    if (transition.at <= day(onDate)) held = transition.to || held
    else break
  }
  return held
}

export function dueOn(timeline: DueTimelineEntry[], currentDue: string | null, onDate: string): string | null {
  if (!timeline.length) return currentDue ? parseDate(currentDue) : null
  let held = timeline[0].from
  for (const event of timeline) {
    if (event.at <= day(onDate)) held = event.to
    else break
  }
  return held
}

export function openOn(
  entries: ChangelogEntry[],
  currentStatus: string,
  onDate: string,
  categoryOf: Record<string, string | undefined>,
): boolean | null {
  const category = categoryOf[statusOn(entries, currentStatus, onDate)]
  return category === undefined ? null : category !== 'done'
}

export function itemHistory(entries: ChangelogEntry[], currentDue?: string | null): ItemHistory {
  const due = dueHistory(entries, currentDue)
  const assigneeTimeline = transitions(entries, 'assignee')
  return {
    due,
    enteredRegister: enteredRegister(entries),
    labelRemoved: labelRemoved(entries),
    assigneeChanges: assigneeTimeline.length,
    assigneeTimeline,
    scoreEvents: transitions(entries, 'customfield_16104'),
    categoryEvents: transitions(entries, 'customfield_16137'),
    statusTimeline: transitions(entries, 'status'),
    projectMoves: transitions(entries, 'project'),
  }
}
